import type { StandardServicePageContent } from "~/content/servicePage";

export const EnergyStorageInspectionContent: StandardServicePageContent = {
  heroTitle: "Przegląd magazynu energii",
  quickFactsTitle: "Najważniejsze informacje w skrócie",
  quickFacts: [
    {
      label: "Kiedy warto",
      value: "Po uruchomieniu, rozbudowie PV i przed końcem gwarancji",
      description:
        "Kontrolę dobrze wykonać także po zmianie falownika, po dłuższym postoju systemu i zawsze wtedy, gdy pojawiają się alarmy lub problemy z ładowaniem.",
    },
    {
      label: "Co obejmuje",
      value: "BMS, połączenia, zabezpieczenia i chłodzenie",
      description:
        "Badanie dotyczy nie tylko samej baterii, ale też komunikacji z falownikiem, historii alarmów, warunków pracy urządzenia i poprawności konfiguracji całego układu.",
    },
    {
      label: "Na co reagować",
      value: "Alarmy, spadek pojemności i nietypowe nagrzewanie",
      description:
        "To najczęstsze sygnały, że magazyn nie pracuje stabilnie. Szybka diagnoza zwykle zmniejsza ryzyko kosztownej awarii lub problemów z całą instalacją PV.",
    },
    {
      label: "Ile kosztuje",
      value: "Cena zależy od systemu i poziomu integracji",
      description:
        "Prosty domowy magazyn wycenia się inaczej niż układ backupowy z dodatkowymi zabezpieczeniami i rozbudowaną automatyką. Znaczenie ma też lokalizacja oraz zakres raportu.",
    },
  ],
  faqTitle: "Najczęstsze pytania o przegląd magazynu energii",
  sections: [
    {
      title: "Na czym polega przegląd magazynu energii",
      paragraphs: [
        `Przegląd magazynu energii polega na ocenie, czy cały system bateryjny pracuje bezpiecznie i stabilnie w warunkach, do których został zaprojektowany. Nie chodzi wyłącznie o sprawdzenie, czy aplikacja pokazuje poprawne dane, ale o ocenę połączeń elektrycznych, zabezpieczeń, elektroniki sterującej oraz warunków pracy samego urządzenia.`,
        `Magazyn energii pracuje na styku instalacji elektrycznej budynku, falownika, systemu BMS i często także instalacji fotowoltaicznej. Jeśli którykolwiek z tych elementów zaczyna działać niestabilnie, skutki mogą wykraczać poza spadek wydajności i dotyczyć bezpieczeństwa całego układu.`,
        `Dobrze wykonany przegląd ma dać właścicielowi jasną odpowiedź, czy system pracuje prawidłowo, czy wymaga dalszej obserwacji, czy też potrzebna jest interwencja serwisu, instalatora lub elektryka odpowiedzialnego za resztę instalacji.`,
      ],
    },
    {
      title: "Co obejmuje kontrola baterii, BMS i współpracy z falownikiem",
      paragraphs: [
        `Kontrola magazynu energii obejmuje zwykle ocenę połączeń elektrycznych, aparatury zabezpieczającej, stanu przewodów, komunikacji z falownikiem i systemem BMS oraz warunków montażu urządzenia. Sprawdza się też podstawowe parametry pracy, historię alarmów, warunki chłodzenia i dostępność serwisową systemu.`,
        `W praktyce ważne jest nie tylko to, czy magazyn działa, ale jak zachowuje się pod obciążeniem i w codziennym cyklu ładowania oraz rozładowania. To właśnie wtedy wychodzą problemy z niestabilną komunikacją, przegrzewaniem, błędną konfiguracją albo spadkiem sprawności modułów.`,
        `Po kontroli właściciel powinien otrzymać informację, czy system pracuje prawidłowo, które elementy wymagają dalszej obserwacji i czy potrzebne są dodatkowe pomiary, aktualizacja konfiguracji lub interwencja producenta.`,
      ],
    },
    {
      title: "Kiedy wykonać przegląd magazynu energii i jakie objawy są alarmowe",
      paragraphs: [
        `Przegląd warto wykonać po uruchomieniu nowego systemu, po rozbudowie instalacji PV, po zmianie falownika albo przed końcem gwarancji. To dobry moment, żeby wychwycić błędy montażowe, konfiguracyjne i problemy, które nie są jeszcze widoczne w codziennym działaniu całego domu lub firmy.`,
        `Szybkiej kontroli wymagają alarmy systemowe, spadek pojemności, problemy z ładowaniem i rozładowaniem, przerwy w komunikacji z falownikiem oraz nietypowe nagrzewanie urządzenia. W takich sytuacjach nie warto czekać, aż problem przełoży się na pełną awarię albo utratę funkcji zasilania awaryjnego.`,
        `Jeśli magazyn współpracuje z fotowoltaiką, kontrola powinna obejmować także integrację z resztą systemu. Część usterek wynika nie z samej baterii, lecz z konfiguracji falownika, zabezpieczeń, rozdzielni albo logiki sterowania całym układem.`,
      ],
    },
    {
      title: "Cena przeglądu magazynu energii",
      paragraphs: [
        `Cena przeglądu magazynu energii zależy od typu systemu, pojemności, producenta, poziomu integracji z falownikiem i instalacją PV oraz zakresu kontroli. Inaczej wycenia się prosty system domowy, a inaczej rozbudowany układ z funkcją backupu i dodatkowymi komponentami.`,
        `Na koszt wpływa również to, czy kontrola obejmuje tylko podstawowe oględziny, czy także analizę alarmów, ocenę współpracy z falownikiem, dodatkowe pomiary i przygotowanie szczegółowego raportu. Im większa złożoność systemu, tym większy nakład pracy po stronie wykonawcy.`,
        `Porównując oferty, warto sprawdzić, czy cena obejmuje ocenę konfiguracji, kontrolę zabezpieczeń, analizę historii pracy i konkretne wnioski po przeglądzie. Lokalny rynek ma znaczenie, ale o wartości usługi decyduje przede wszystkim to, czy diagnoza obejmuje cały system, a nie tylko samą obudowę magazynu.`,
      ],
    },
  ],
  scopeTitle: "Zakres usług przeglądu magazynu energii",
  scopeItems: [
    "Ocena stanu baterii",
    "Kontrola systemu BMS",
    "Sprawdzenie połączeń DC/AC",
    "Analiza alarmów i błędów",
    "Ocena chłodzenia i wentylacji",
    "Weryfikacja komunikacji z falownikiem",
    "Kontrola zabezpieczeń",
    "Raport z przeglądu",
  ],
  seo: {
    title: "Przegląd magazynu energii: cena, BMS, alarmy i współpraca z PV",
    description:
      "Zamów przegląd magazynu energii i sprawdź stan baterii, BMS, zabezpieczeń oraz współpracy z falownikiem i PV. Zobacz, kiedy wykonać kontrolę i co wpływa na cenę.",
  },
};
